import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, SearchField } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  AccountsListPageDocument,
  BankingAccountDetailedFragment,
  BankingBankAccountFragment,
  BankingOnboardingFragment,
  LegalEntityForSelectFragment,
  ReconnectBankDocument,
} from 'generated/resources/graphql'

import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AccountCardList } from 'modules/banking/components/AccountCardList'
import { AccountLoadingCard } from 'modules/banking/components/AccountLoadingCard'
import { AccountOnboardingCardList } from 'modules/banking/components/AccountOnboardingCardList'
import { AccountsUpsellCard } from 'modules/banking/components/AccountsUpsellCard'
import { FininshSetupAccountModal } from 'modules/banking/components/FinishSetupAccountModal'
import { NewAccountModal } from 'modules/banking/components/NewAccountModal'
import { useBankingAuthorization } from 'modules/banking/hooks/useBankingAuthorization'
import { bankingAccountActions, getContinueOnboardingUrl } from 'modules/banking/utils'
import { accountsPayableUpsellRequestProps } from 'modules/finance/lib/upsellUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface AccountsFilterProps {
  search: string
  onSearch: (value: string) => void
}

const AccountsFilter = ({ search, onSearch }: AccountsFilterProps) => {
  return (
    <Box flexDirection="row" width="s140" justifyContent="flexEnd">
      <SearchField
        search={search}
        variant="regular"
        collapsable
        placeholder={i18n.t('common.search')}
        onSearch={(ev) => onSearch(ev.target.value)}
      />
    </Box>
  )
}

interface AccountsActionsProps {
  onAddAccount?: () => void
}

const AccountsActions = ({ onAddAccount }: AccountsActionsProps) => {
  if (!onAddAccount) return null

  return (
    <Button onClick={onAddAccount} type="primary">
      {i18n.t('banking.accounts.actions.add_account')}
    </Button>
  )
}

interface SearchParamsType {
  setup_membership?: string
}

const AccountsListPage = () => {
  const {
    company: { id: companyId },
    employee,
  } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const searchParams = useSearchParams<SearchParamsType>()
  const [search, onSearch] = useState<string>('')
  const [showNewModal, setShowNewModal] = useState<boolean>(false)
  const [showUpsellModal, setShowUpsellModal] = useState<boolean>(false)
  const [showFinishSetupModal, setShowFinishSetupModal] = useState<boolean>(false)
  const [selectedAccount, setSelectedAccount] = useState<
    BankingAccountDetailedFragment | undefined
  >()
  const [selectedBankAccount, setSelectedBankAccount] = useState<
    BankingBankAccountFragment | undefined
  >()
  const [selectedLegalEntity, setSelectedLegalEntity] = useState<LegalEntityForSelectFragment>()
  const { getAuthorizationUrl } = useBankingAuthorization()
  const membershipSetupProcessed = useRef(false)

  const { data, loading, error } = useQuery(AccountsListPageDocument, {
    variables: {
      companyId,
      featureIds: [Features.accounts_payable_management, Features.finance_payments_core],
    },
  })
  const [reconnectBank] = useMutation(ReconnectBankDocument, {
    extractorFn: (mutationData) =>
      mutationData.bankingMutations.reconnectExternalBankBankingOnboarding,
  })

  const accounts = data?.banking.accounts || []
  const onboardings = data?.banking.onboardings || []
  const currencies = data?.apiCore.currencies || []
  const legalEntities = data?.companies.legalEntitiesConnection?.nodes || []
  const { bankAccountsManage, bankAccountsSeeDetailPage } = data?.banking._policies || {
    bankAccountsManage: { can: false },
    bankAccountsSeeDetailPage: { can: false },
  }
  const { sidebarSeeFinanceAccountsSubLevelItem } = data?.finance._policies || {
    sidebarSeeFinanceAccountsSubLevelItem: { can: false },
  }
  const accountsPayableAvailable =
    data?.companies.featureAvailabilitiesConnection?.nodes.find(
      (node) => node.id === Features.accounts_payable_management
    )?.available ?? false

  const paymentsFeatureAvailable =
    data?.companies.featureAvailabilitiesConnection?.nodes.find(
      (node) => node.id === Features.finance_payments_core
    )?.available ?? false

  const features = data?.features?.featuresConnection?.nodes ?? []

  const swanEnabled =
    features.find((feature) => feature.name === 'dev_finance_swan_reactivation')?.enabled ?? false

  // Handle setup_membership parameter from invitation email
  useEffect(() => {
    const setupMembershipId = searchParams.setup_membership

    // Only process once per page load
    if (setupMembershipId && employee?.id && !membershipSetupProcessed.current) {
      membershipSetupProcessed.current = true

      const handleMembershipSetup = async () => {
        try {
          app.setProgress({
            message: i18n.t('banking.accounts.membership_setup.authorizing'),
          })

          const currentUrl = window.location.origin + window.location.pathname
          const authorizationUrl = await getAuthorizationUrl(employee.id, currentUrl, {
            action: 'bind_account_membership',
            targetId: parseInt(setupMembershipId, 10),
          })

          if (authorizationUrl) {
            // Redirect to Swan for authorization
            window.location.href = authorizationUrl
          } else {
            app.setFlash({
              type: 'wrong',
              message: i18n.t('banking.accounts.membership_setup.error'),
            })
            app.setProgress(null)
          }
        } catch (setupError) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('banking.accounts.membership_setup.error'),
          })
          app.setProgress(null)
        }
      }

      handleMembershipSetup()
    }
  }, [searchParams.setup_membership, employee?.id, getAuthorizationUrl])

  const handleReconnect = async (account: BankingAccountDetailedFragment) => {
    if (!account.institution?.id || !account.onboarding.legalEntity.id) {
      return
    }

    app.setProgress({
      message: i18n.t('banking.accounts.new_wizard.notifications.connecting_bank'),
    })

    const result = await reconnectBank({
      variables: {
        institutionId: account.institution.id,
        legalEntityId: account.onboarding.legalEntity.id,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('banking.accounts.new_wizard.notifications.error'),
      })
    })

    if (handled.success) {
      if (handled.data.onboarding?.onboardingUrl) {
        window.open(handled.data.onboarding.onboardingUrl, '_self')
      }
    }
  }

  const onCardClick = (
    account?: BankingAccountDetailedFragment,
    bankAccount?: BankingBankAccountFragment,
    onboarding?: BankingOnboardingFragment
  ) => {
    if (account && bankAccount) {
      const { hasToFinishSetup, hasToReconnect } = bankingAccountActions(account, bankAccount)
      setSelectedAccount(account)
      setSelectedBankAccount(bankAccount)

      if (hasToReconnect) {
        handleReconnect(account)
      } else if (hasToFinishSetup) {
        setShowFinishSetupModal(true)
      } else {
        if (bankAccountsSeeDetailPage.can) {
          navigate(navigation.finance.banking.accounts.accountId.fullPath({ id: bankAccount.id }))
        }
      }
    } else if (onboarding?.onboardingUrl) {
      const url = getContinueOnboardingUrl(onboarding, [])
      if (url) {
        window.open(url, '_self')
      }
    }
  }
  const hasAccountWithImportDataService =
    !loading &&
    !error &&
    !!accounts.find((el) => el.providerConfiguration?.services.includes('import_data'))
  const showAccountsPayableUpsellCard =
    !accountsPayableAvailable || !hasAccountWithImportDataService
  const upsellCardAction = accountsPayableAvailable ? 'add_account' : 'contact_us'
  const upsellRequestProps = accountsPayableUpsellRequestProps('ap_bankaccounts_upsellcard')

  return (
    <ResponsivePage.Body
      showHeader
      variant="full"
      loading={loading}
      authorized={sidebarSeeFinanceAccountsSubLevelItem.can}
      error={error}
    >
      <>
        {showNewModal && (
          <NewAccountModal
            legalEntities={legalEntities}
            currencies={currencies}
            onClose={() => setShowNewModal(false)}
            accountingEnabled={accountsPayableAvailable}
            existingOnboardings={onboardings}
            showAccountPurposeSelector={swanEnabled}
          />
        )}
        {showUpsellModal && (
          <UpsellingKit.FeatureModal
            moduleRequested={upsellRequestProps.moduleRequested}
            requestSource="ap_bankaccounts_upsellcard"
            requestReason={upsellRequestProps.requestReason}
            featureRequested={
              upsellRequestProps.featureRequested || Features.accounts_payable_management
            }
            isOpen={showUpsellModal}
            onClose={() => setShowUpsellModal(false)}
          />
        )}
        {showFinishSetupModal && selectedBankAccount && selectedAccount && (
          <FininshSetupAccountModal
            bankAccount={selectedBankAccount}
            legalEntityId={selectedAccount.legalEntity.id}
            companyId={companyId}
            onClose={() => setShowFinishSetupModal(false)}
            onFinish={() => setShowFinishSetupModal(false)}
          />
        )}
        {showAccountsPayableUpsellCard && (
          <Box width="full" paddingBottom={'s48'}>
            <AccountsUpsellCard
              mode={upsellCardAction}
              onAddAccountClick={() => {
                setShowUpsellModal(false)
                setShowNewModal(true)
              }}
              onContactUsClick={() => {
                setShowUpsellModal(true)
              }}
            />
          </Box>
        )}
        <Box
          width="full"
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          paddingBottom="s16"
        >
          <Box width="s300">
            <LegalEntitySelector
              onChange={(entity) => setSelectedLegalEntity(entity ?? undefined)}
              value={selectedLegalEntity ?? null}
              multiple={false}
              width="s300"
              hideLabel
              emptyLabel={i18n.t('common.legal_entity')}
              label={i18n.t('common.legal_entity')}
            />
          </Box>
          <Box flexDirection="row" gap="s8">
            <AccountsFilter search={search} onSearch={onSearch} />
            <AccountsActions
              onAddAccount={
                bankAccountsManage.can && (accountsPayableAvailable || paymentsFeatureAvailable)
                  ? () => setShowNewModal(true)
                  : undefined
              }
            />
          </Box>
        </Box>
        {loading ? (
          <Grid variant="medium">
            <AccountLoadingCard />
            <AccountLoadingCard />
            <AccountLoadingCard />
            <AccountLoadingCard />
            <AccountLoadingCard />
            <AccountLoadingCard />
          </Grid>
        ) : (
          <Grid variant="medium">
            <AccountOnboardingCardList
              onboardings={onboardings}
              search={search}
              selectedLegalEntity={selectedLegalEntity}
            />
            <AccountCardList
              onCardClick={onCardClick}
              accounts={accounts}
              search={search}
              selectedLegalEntity={selectedLegalEntity}
            />
          </Grid>
        )}
      </>
    </ResponsivePage.Body>
  )
}

export default AccountsListPage
